/**
 * Store Pinia `espace-factures` — état de l'espace client « Mes factures ».
 *
 * Régime invoice-only (aucune anomalie/verdict). Sert deux surfaces avec le
 * même contrat : l'espace company-wide et la carte « Factures » de la page
 * compteur (via le filtre `pdl`).
 *
 * Mode mock : `VITE_USE_MOCK_FACTURES=true` retourne les fixtures locales,
 * exactement au format de `GET /v5/energyinvoices`.
 */

import { defineStore } from "pinia"
import { ref } from "vue"
import { FacturesEspaceService } from "@modules/courtage/services/factures-espace.service"
import type {
    FactureItem,
    FacturesAgregats,
    FacturesListResponse,
    FacturesQuery,
    FactureUploadJob,
} from "@modules/courtage/types/facture-espace"
import { facturesEspaceMock } from "./espace-factures.mock-data"

const USE_MOCK = import.meta.env.VITE_USE_MOCK_FACTURES === "true"
const service = new FacturesEspaceService()

export const useEspaceFacturesStore = defineStore(
    "courtage-espace-factures",
    () => {
        const factures = ref<FactureItem[]>([])
        const agregats = ref<FacturesAgregats | null>(null)
        const nextCursor = ref<string | null>(null)
        const loading = ref(false)
        const loadingMore = ref(false)
        const error = ref<string | null>(null)
        // Dernier filtre appliqué — réutilisé par `fetchMore` (pagination curseur).
        const lastQuery = ref<FacturesQuery>({})

        /**
         * Charge (remplace) les factures. Sans `pdl` → company-wide ; avec `pdl`
         * → un compteur. Réinitialise le curseur : c'est l'entrée canonique au
         * changement de filtre (recherche / fournisseur / statut / période).
         */
        async function fetchList(query: FacturesQuery = {}): Promise<void> {
            loading.value = true
            error.value = null
            lastQuery.value = query
            try {
                const res: FacturesListResponse = USE_MOCK
                    ? await mockDelayed(query)
                    : await service.list(query)
                factures.value = res.factures
                agregats.value = res.agregats
                nextCursor.value = res.next_cursor
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : String(err)
                error.value = `Erreur chargement des factures : ${msg}`
                factures.value = []
                agregats.value = null
                nextCursor.value = null
            } finally {
                loading.value = false
            }
        }

        /**
         * Charge la page suivante (pagination curseur) et l'AJOUTE à la liste,
         * en conservant le dernier filtre. No-op s'il n'y a pas de page suivante
         * ou si un chargement est déjà en cours. Les agrégats (calculés sur tout
         * le filtre, pas la page) ne bougent pas.
         */
        async function fetchMore(): Promise<void> {
            if (!nextCursor.value || loading.value || loadingMore.value) return
            loadingMore.value = true
            error.value = null
            try {
                const query: FacturesQuery = {
                    ...lastQuery.value,
                    cursor: nextCursor.value,
                }
                const res: FacturesListResponse = USE_MOCK
                    ? await mockDelayed(query)
                    : await service.list(query)
                factures.value = [...factures.value, ...res.factures]
                nextCursor.value = res.next_cursor
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : String(err)
                error.value = `Erreur chargement des factures : ${msg}`
            } finally {
                loadingMore.value = false
            }
        }

        /**
         * Ouvre le scan ORIGINAL d'une facture détenue dans un nouvel onglet.
         * L'onglet est ouvert SYNCHRONEMENT (geste utilisateur) puis navigué une
         * fois l'URL présignée obtenue, pour ne pas être bloqué par le navigateur.
         * No-op si la facture n'a pas de PDF (le bouton n'est de toute façon offert
         * que si `pdf_disponible`).
         */
        async function openPdf(facture: FactureItem): Promise<void> {
            if (!facture.pdf_disponible) return
            if (USE_MOCK) {
                window.alert(
                    "Aperçu du PDF indisponible en mode démonstration (mock)."
                )
                return
            }
            const tab = window.open("", "_blank")
            try {
                const { url } = await service.getPdfUrl(facture.id)
                if (tab) tab.location.href = url
                else window.open(url, "_blank", "noopener")
            } catch (err: unknown) {
                if (tab) tab.close()
                const msg = err instanceof Error ? err.message : String(err)
                error.value = `Impossible d'ouvrir le PDF : ${msg}`
            }
        }

        /**
         * Dépose des PDF puis SUIT le parse asynchrone (poll borné), et
         * rafraîchit la liste à la fin (les factures déposées apparaissent).
         * Régime invoice-only : le job ne renvoie que des compteurs (R1).
         * Retourne l'état final du job ; lève en cas d'erreur réseau/budget.
         */
        async function uploadEtSuivre(
            files: File[]
        ): Promise<FactureUploadJob> {
            if (USE_MOCK) {
                window.alert(
                    "Dépôt de factures indisponible en mode démonstration (mock)."
                )
                throw new Error("Dépôt indisponible en mode démonstration.")
            }
            const ack = await service.upload(files)
            const TERMINAL = ["done", "done_with_errors", "error"]
            const POLL_MS = 2000
            const MAX_TRIES = 90 // ~3 min de marge pour 10 PDF
            let job = await service.getJob(ack.job_id)
            let tries = 0
            while (!TERMINAL.includes(job.status) && tries < MAX_TRIES) {
                await new Promise((r) => setTimeout(r, POLL_MS))
                job = await service.getJob(ack.job_id)
                tries++
            }
            // Les factures déposées (et parsées) entrent dans la liste.
            await fetchList()
            return job
        }

        return {
            factures,
            agregats,
            nextCursor,
            loading,
            loadingMore,
            error,
            fetchList,
            fetchMore,
            openPdf,
            uploadEtSuivre,
        }
    }
)

async function mockDelayed(
    query: FacturesQuery
): Promise<FacturesListResponse> {
    await new Promise((r) => setTimeout(r, 200))
    return facturesEspaceMock(query)
}
